import React, { useEffect, useMemo, useState } from 'react';
import cl from 'classnames';
import { useParams } from 'react-router-dom';
import { HeadsetSubscription } from 'src/models';
import { HEADSETS } from 'src/constants';
import { useContextData, useLanguage, usePage } from 'src/hooks';
import {
  formatDatetime,
  formatDuration,
  formatPaymentInitiator,
} from 'src/helper';
import { useAppDispatch } from 'src/store';
import {
  useHeadsetById,
  fetchHeadsetSubscriptionsAction,
} from 'src/services/headsets';
import {
  PageSwitcher,
  StickyTable,
  useStickyCols,
  StickyTableConfig,
} from 'src/components';
import {
  FilterModal,
  FilterModalButton,
  Tooltip,
} from 'src/components/general';
import styles from './headsetSubscriptionsStyle.module.scss';

export const HeadsetSubscriptions = () => {
  const { hotel: companyId, headset: headsetId } = useParams() as {
    hotel: string;
    headset: string;
  };
  const {
    header: { width: headerWidth },
  } = useContextData();
  const {
    access: { accessMap },
  } = usePage();
  const dispatch = useAppDispatch();
  const { t, td } = useLanguage();
  const headset = useHeadsetById(headsetId);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean | null>(null);

  const subscriptions = useMemo(
    () => headset?.subscriptions ?? [],
    [headset?.subscriptions],
  );

  const headsetIsLoaded = useMemo(() => Boolean(headset), [headset]);

  useEffect(() => {
    if (!headsetIsLoaded) {
      return;
    }

    setIsLoading(true);

    dispatch(fetchHeadsetSubscriptionsAction({ companyId, headsetId })).then(
      () => {
        setIsLoading(false);
      },
    );
  }, [dispatch, headsetIsLoaded, headsetId, companyId]);

  const config: StickyTableConfig<HeadsetSubscription>[] = useMemo(
    () => [
      {
        title: 'ID',
        key: 'id',
        field: 'id',
        render: (value) => (
          <Tooltip
            text={`ID: ${value}`}
            tooltipClassName={styles.tooltip}
            arrowClassName={styles.tooltipArrow}
            withClipboard
          >
            {value.slice(-12)}
          </Tooltip>
        ),
      },
      {
        title: t('page.headset.subscription.tariff'),
        key: 'price',
        field: 'price',
      },
      {
        title: t('page.headset.subscription.duration'),
        key: 'duration',
        field: 'duration',
        render: (value) => formatDuration(value),
      },
      {
        title: t('page.headset.subscription.paymentInitiator'),
        key: 'paymentInitiator',
        field: 'paymentInitiator',
        render: (value) => td(formatPaymentInitiator(value)),
      },
      {
        title: t('page.headset.subscription.createdAt'),
        key: 'createdAt',
        field: 'createdAt',
        render: (value) => (
          <div className={styles.tag}>{formatDatetime(value)}</div>
        ),
      },
      {
        title: t('page.headset.subscription.canceledAt'),
        key: 'canceledAt',
        field: 'canceledAt',
        render: (value) => (
          <div className={styles.tag}>{formatDatetime(value)}</div>
        ),
      },
    ],
    [t, td],
  );

  const {
    stickyCols,
    stickyColsFilter,
    modalStickyColsFilter,
    applySCChanges,
    resetSCChanges,
  } = useStickyCols({
    config,
    className: styles.stickyCols,
    modalClassName: styles.modalStickyCols,
  });

  return (
    <div className={styles.container}>
      <FilterModal
        show={showFiltersModal}
        close={() => {
          setShowFiltersModal(false);
        }}
        onApply={() => {
          applySCChanges();
          setShowFiltersModal(false);
        }}
        onCancel={() => {
          resetSCChanges();
        }}
      >
        {modalStickyColsFilter}
      </FilterModal>
      <div
        className={styles.topSection}
        style={{ minWidth: headerWidth, maxWidth: headerWidth }}
      >
        <div
          className={cl(
            styles.pageSwitcherContainer,
            accessMap[HEADSETS].length === 1 && styles.inactive,
          )}
        >
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
          <div className={styles.filterButtons}>
            <FilterModalButton setShow={setShowFiltersModal} />
          </div>
        </div>
        <div className={styles.filters}>{stickyColsFilter}</div>
      </div>
      <StickyTable
        config={config}
        data={subscriptions}
        withPaginator
        isLoading={isLoading}
        stickyCols={stickyCols}
        className={styles.table}
      />
    </div>
  );
};

export default HeadsetSubscriptions;
